import { tC } from "../../utils/TranslateWithContentLanguage";
import { TicketCheck, CheckCircle, RefreshCw } from "lucide-react";
import CardImage from "../tool_components/CardImage";
import ExitTicketCardPdf from "./ExitTicketCardPdf";
import { Button, Text } from "@core/index";
import PdfDownloadButton from "../pdf/PdfDownloadButton";
import useImagePdf from "../pdf/useImagePdf";
import { MascotBubble } from "../tool_components/MascotBubble";

import { useState } from "react";
import { useAppSelector } from "@packages/store/hooks";
import PdfCustomizationModal, {
  PdfCustomizationSettings,
} from "@features/common/components/pdf-customization-modal";
import {
  getGetExitTicketGeneratorQueryKey,
  getGetUserExitTicketGeneratorsQueryKey,
  updateExitTicketGenerator,
} from "@packages/api/madlenAPI";
import useIsAuthor from "@packages/hooks/useIsAuthor";
import RegeneratingOverlay from "../tool_components/RegenLoadingOverlay";
import { ExitTicketGeneratorResponseDtoStatus } from "@packages/api/madlenAPI.schemas";

import EditableMarkdown from "../tool_components/EditableMarkdown";
import { useQueryClient } from "@tanstack/react-query";

export interface ExitTicket {
  ticket_title: string;
  ticket_prompt: string;
  possible_responses: string[] | null;
  recommended_format: string;
}

export interface ExitTicketCardProps {
  exitTickets: ExitTicket[];
  isImageLoading?: boolean;
  imageUrl?: string;
  videoUrl?: string;
  courseName: string;
  gradeLevel: number;
  topic: string;
  createdAt: string;
  toolId: number;
  authorName: string;
  authorId?: number;
  status?: ExitTicketGeneratorResponseDtoStatus;
  customizationSettings: PdfCustomizationSettings;
  isEditable?: boolean;
  onCreateAgain?: () => void;
  contentLanguage: string;
}

export const ExitTicketCard = ({
  exitTickets,
  status,
  isImageLoading,
  imageUrl,
  videoUrl,
  courseName,
  gradeLevel,
  topic,
  createdAt,
  toolId,
  authorName,
  authorId,
  onCreateAgain,
  isEditable = false,
  contentLanguage,
}: Omit<ExitTicketCardProps, "customizationSettings">) => {
  const finalImageUrl = useImagePdf(imageUrl);

  const [showPdfCustomizationModal, setShowPdfCustomizationModal] =
    useState(false);
  const customizationSettings = useAppSelector(
    (state) => state.user.teacher?.preferences?.pdfOptions,
  );
  const isAuthor = useIsAuthor(authorId);
  const queryClient = useQueryClient();

  return (
    <div className="space-y-6">
      {isAuthor && (
        <MascotBubble
          toolsQueryKey={getGetUserExitTicketGeneratorsQueryKey()}
          toolQueryKey={getGetExitTicketGeneratorQueryKey(toolId)}
          createChatParams={{
            teacherToolId: toolId,
            toolType: "EXIT_TICKET_GENERATOR",
          }}
        />
      )}
      <CardImage
        createdAt={createdAt}
        theme="mint"
        imageUrl={imageUrl}
        videoUrl={videoUrl}
        isLoading={isImageLoading}
        courseName={courseName}
        gradeLevel={gradeLevel}
        topic={
          <div className="flex items-center gap-4 justify-center">
            <div>
              <Text variant={"h1"} className="mb-2">
                {topic}
              </Text>
            </div>
          </div>
        }
        contentLanguage={contentLanguage}
      />
      <div className="flex justify-end gap-2 mt-2">
        {onCreateAgain && (
          <Button
            onClick={onCreateAgain}
            variant="solid"
            size="md"
            textColor="text-white dark:text-white"
            bgColor="bg-gradient-to-r from-green-400 to-teal-500 dark:from-green-300 dark:to-teal-400 hover:from-green-500 hover:to-teal-600 dark:hover:from-green-400 dark:hover:to-teal-500"
            rounded="xl"
          >
            <RefreshCw size={18} />
            <span>{tC(contentLanguage, "common.createAnother")}</span>
          </Button>
        )}
        <PdfDownloadButton
          pdfTitle={topic}
          document={
            <ExitTicketCardPdf
              toolId={toolId}
              customizationSettings={customizationSettings}
              exitTickets={exitTickets}
              courseName={courseName}
              gradeLevel={gradeLevel}
              topic={topic}
              imageUrl={finalImageUrl}
              authorName={authorName}
              createdAt={createdAt}
            />
          }
          openCustomizationModal={() => setShowPdfCustomizationModal(true)}
          btnTheme="bg-gradient-to-r from-green-400 to-teal-500 dark:from-green-300 dark:to-teal-400"
        />
      </div>

      <div className="space-y-4 relative">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {exitTickets?.map((ticket, index) => (
            <div key={index} className="flex flex-col">
              <div className="flex flex-row items-center gap-2 text-gray-900 dark:text-gray-100 rounded-xl p-4">
                <span className="p-2 rounded-xl bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-300">
                  <TicketCheck size={18} />
                </span>
                <h5 className="font-semibold text-lg">
                  <EditableMarkdown
                    value={ticket.ticket_title}
                    isEditable={isEditable}
                    handleSave={async (newValue) => {
                      if (!exitTickets) return;

                      const updatedExitTickets = [...exitTickets];
                      updatedExitTickets[index] = {
                        ...updatedExitTickets[index],
                        ticket_title: newValue,
                      };

                      await updateExitTicketGenerator(toolId, {
                        exitTickets: updatedExitTickets,
                      });

                      await queryClient.invalidateQueries({
                        queryKey: getGetUserExitTicketGeneratorsQueryKey(),
                      });
                      await queryClient.invalidateQueries({
                        queryKey: getGetExitTicketGeneratorQueryKey(toolId),
                      });
                    }}
                  />
                </h5>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-6 space-y-4 flex-1 hover:bg-green-50 dark:hover:bg-green-900/20">
                <div className="prose dark:prose-invert max-w-none">
                  <div className="leading-relaxed">
                    <EditableMarkdown
                      value={ticket.ticket_prompt}
                      isEditable={isEditable}
                      handleSave={async (newValue) => {
                        if (!exitTickets) return;

                        const updatedExitTickets = [...exitTickets];
                        updatedExitTickets[index] = {
                          ...updatedExitTickets[index],
                          ticket_prompt: newValue,
                        };

                        await updateExitTicketGenerator(toolId, {
                          exitTickets: updatedExitTickets,
                        });

                        await queryClient.invalidateQueries({
                          queryKey: getGetUserExitTicketGeneratorsQueryKey(),
                        });
                        await queryClient.invalidateQueries({
                          queryKey: getGetExitTicketGeneratorQueryKey(toolId),
                        });
                      }}
                    />
                  </div>
                </div>
                {ticket.recommended_format && (
                  <div className="mt-4">
                    <h6 className="text-sm font-bold text-gray-800 dark:text-gray-200">
                      {tC(contentLanguage, "exitTickets.recommendedFormat")}:
                    </h6>
                    <div className="mt-1 text-sm">
                      <EditableMarkdown
                        value={ticket.recommended_format}
                        isEditable={isEditable}
                        handleSave={async (newValue) => {
                          if (!exitTickets) return;

                          const updatedExitTickets = [...exitTickets];
                          updatedExitTickets[index] = {
                            ...updatedExitTickets[index],
                            recommended_format: newValue,
                          };

                          await updateExitTicketGenerator(toolId, {
                            exitTickets: updatedExitTickets,
                          });

                          await queryClient.invalidateQueries({
                            queryKey: getGetUserExitTicketGeneratorsQueryKey(),
                          });
                          await queryClient.invalidateQueries({
                            queryKey: getGetExitTicketGeneratorQueryKey(toolId),
                          });
                        }}
                      />
                    </div>
                  </div>
                )}
                {ticket.possible_responses && (
                  <div className="mt-4">
                    <h6 className="text-sm font-bold text-gray-800 dark:text-gray-200">
                      {tC(contentLanguage, "exitTickets.possibleResponses")}:
                    </h6>
                    <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 text-sm mt-1">
                      {ticket.possible_responses.map(
                        (response, responseIdx) => (
                          <li
                            key={responseIdx}
                            className="flex items-start gap-2 flex-row"
                          >
                            <CheckCircle
                              size={16}
                              className="text-green-600 dark:text-green-300 flex-shrink-0 mt-1"
                            />
                            <span className="break-words">
                              {response.charAt(0).toUpperCase() +
                                response.slice(1)}
                            </span>
                          </li>
                        ),
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        {status === "REGENERATING" && <RegeneratingOverlay />}
      </div>
      {/* PDF Customization Modal */}
      <PdfCustomizationModal
        show={showPdfCustomizationModal}
        onClose={() => setShowPdfCustomizationModal(false)}
        hideSchoolLogoOptions
        documentComponent={
          <ExitTicketCardPdf
            toolId={toolId}
            customizationSettings={customizationSettings}
            exitTickets={exitTickets}
            courseName={courseName}
            gradeLevel={gradeLevel}
            topic={topic}
            imageUrl={finalImageUrl}
            authorName={authorName}
            createdAt={createdAt}
          />
        }
      />
    </div>
  );
};
